// CPI-974: the rules behind the CRM's cancel-booking form — a mandatory category and a mandatory
// written reason, Vietnam only. Why each rule is what it is, and why the helpers are here rather
// than in the modal, are in docs/merge-audit-main-svn-to-master.md, "Vietnam-only notes worth
// keeping (2026-09-06, CPI-974)".

// Equal to CANCELLATION_REASON_MAX_LENGTH in the Showroom's cancel modal.
export const CANCELLATION_REASON_MAX_LENGTH = 500;

export const CANCEL_BOOKING_BANK_FIELDS = [
  'bank_name',
  'bank_account_number',
  'bank_account_name',
];

// Returns error codes, not sentences; the modal resolves each one to a message.
export const validateCancelBookingForm = (form, hasBankFromContact) => {
  const errors = {};
  if (!form.cancellation_type) {
    errors.cancellation_type = 'select_cancellation_type';
  }
  if (!(form.cancellation_reason || '').trim()) {
    errors.cancellation_reason = 'field_required';
  }
  if (!hasBankFromContact) {
    CANCEL_BOOKING_BANK_FIELDS.forEach((key) => {
      if (!(form[key] || '').trim()) {
        errors[key] = 'field_required';
      }
    });
  }
  return errors;
};

// The requestRefund variables: the category travels as sr_cancellation_type, the typed text as
// sr_cancellation_reason.
export const buildRefundVariables = (opportunityId, opportunityData, form) => ({
  opportunity_id: opportunityId,
  sr_cancellation_type: form.cancellation_type,
  sr_cancellation_reason: form.cancellation_reason.trim(),
  sr_status: opportunityData?.sr_status || '',
  customer_id: opportunityData?.customer_id || '',
  bank_name: form.bank_name.trim(),
  bank_account_number: form.bank_account_number.trim(),
  bank_account_name: form.bank_account_name.trim(),
  name: opportunityData?.name || '',
  mobile_country_code: opportunityData?.mobile_country_code || '',
  mobile: opportunityData?.mobile || '',
});

// CPI-1234 AC 9 (added after the 11 Sep DSM; QA comment 162064 reported SSV-000467 as reserved
// yet cancellable): the unit is reserved once the booking is approved, and from `approved`
// onwards the booking can no longer be cancelled from the CRM.
//
// Written as the window in which cancelling is still allowed, not as a list of blocked statuses.
// That window is the `pending_approval` group in mhub-opportunity-api docs/sr_status.md — every
// status before `approved`. So `approved`, everything downstream of it, and any status this list
// has not heard of all disable the button; a new sr_status cannot silently reopen cancellation.
//
// Deliberately NOT the CRM `stage` field: stage flips to 'reserved' the moment a unit is picked
// (see ViewAvailableUnitsSection.shouldShowViewAvailableUnits) and never advances in the VN flow,
// so reading "reserved" off stage would disable the button for every booking that has a unit.
export const CANCELLABLE_SR_STATUSES = [
  'pending_payment',
  'payment_made',
  'partially_paid_unverified',
  'paid_unverified',
  'paid_verified',
];

export const isCancellableSrStatus = (srStatus) => CANCELLABLE_SR_STATUSES.includes(srStatus);

// Why the Cancel booking button is dim, or null when it is live. Returns reason codes, not
// sentences; the page resolves each one to a tooltip. An in-flight cancellation is reported
// ahead of the approved gate: it is the more specific fact, and it is the one an agent can act
// on (wait for the approval) rather than a state of the unit.
export const CANCEL_BOOKING_DISABLED_ALREADY_REQUESTED = 'already_requested';
export const CANCEL_BOOKING_DISABLED_UNIT_RESERVED = 'unit_reserved';

export const getCancelBookingDisabledReason = ({ hasExistingRefund, srStatus }) => {
  if (hasExistingRefund) return CANCEL_BOOKING_DISABLED_ALREADY_REQUESTED;
  if (!isCancellableSrStatus(srStatus)) return CANCEL_BOOKING_DISABLED_UNIT_RESERVED;
  return null;
};
